import styled, { useTheme } from 'styled-components'

import { IconInfo } from 'components/Icons/InfoIcon'
import { Typography } from 'components/Typography/Typography'
import { getAprValue, getSupplyPoolUtilization } from 'modules/MyPositions/utils'
import { ReversedWrapper } from 'components/ReversedWrapper/ReversedWrapper'
import { SupplyPool } from '@kunalabs-io/kai'
import { Tooltip } from 'components/Tooltip/Tooltip'
import Decimal from 'decimal.js'
import { usePositionMetrics } from './Analytics/hooks/usePositionMetrics'
import { PositionDisplayInfo } from 'utils/userUserPositionDisplayInfos'

const PositionBreakdownTitle = styled(Typography)`
  margin: 16px 0;
  display: flex;
  align-items: center;
  & > div {
    display: flex;
    cursor: pointer;
    & > svg {
      margin-left: 6px;
      width: 16px;
      height: 16px;
      & > path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

const PositionBreakdownContainer = styled.div`
  padding: 20px 16px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background.secondary};
`

const PositionBreakdownContainerItem = styled.div`
  display: flex;
  justify-content: space-between;
`

const PositionBreakdownSymbol = styled(Typography)`
  display: flex;
  img {
    margin-right: 8px;
    border-radius: 50%;
  }
`

const ContentContainer = styled.div`
  padding: 16px;
  border-radius: 16px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.background.primary};
  box-shadow: 0px 0px 16px 0px ${p => p.theme.colors.background.hover};

  // @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
  //   padding: 24px;
  // }
`

interface Props {
  position: PositionDisplayInfo
  isReversedPair: boolean
  supplyPoolX: SupplyPool<string, string>
  supplyPoolY: SupplyPool<string, string>
}

const SUPPLY_POOL_WARNING_LIMIT = 0.85

export const ExistingPositionBreakdown = ({ position, isReversedPair, supplyPoolX, supplyPoolY }: Props) => {
  const theme = useTheme()

  const configInfo = position.position.configInfo

  const iconUrlX = configInfo.X.iconUrl
  const iconUrlY = configInfo.Y.iconUrl
  const displaySymbolX = configInfo.X.displaySymbol
  const displaySymbolY = configInfo.Y.displaySymbol
  const supplyPoolXUtilization = supplyPoolX.calcUtilization(Date.now()).toNumber()
  const supplyPoolYUtilization = supplyPoolY.calcUtilization(Date.now()).toNumber()

  const { data: positionMetrics, isLoading } = usePositionMetrics(position.position.id)

  return (
    <ContentContainer>
      <Typography variant="label2" fontWeight="semiBold">
        Position Breakdown
      </Typography>
      <PositionBreakdownTitle variant="body" color="secondary">
        Liquidation Prices
        <Tooltip
          content="The prices at which your position will fall below the liquidation threshold and become susceptible to liquidation."
          position="top"
        >
          <IconInfo width={16} height={16} />
        </Tooltip>
      </PositionBreakdownTitle>
      <PositionBreakdownContainer>
        <PositionBreakdownContainerItem style={{ marginBottom: 20 }}>
          <Typography variant="description" fontWeight="semiBold">
            Low
          </Typography>
          <Typography variant="description" fontWeight="semiBold" style={{ color: theme.colors.text.description }}>
            {position.liquidationPrices &&
              (isReversedPair
                ? position.liquidationPrices[1].inverted().human.toDecimalPlaces(4).toString() || 0
                : position.liquidationPrices[0].human.toDecimalPlaces(4).toString() || 0)}
          </Typography>
        </PositionBreakdownContainerItem>
        <PositionBreakdownContainerItem>
          <Typography variant="description" fontWeight="semiBold">
            High
          </Typography>
          <Typography variant="description" fontWeight="semiBold" style={{ color: theme.colors.text.description }}>
            {position.liquidationPrices &&
              (isReversedPair
                ? position.liquidationPrices[0].inverted().human.toDecimalPlaces(4).toString() || 0
                : position.liquidationPrices[1].human.toDecimalPlaces(4).toString() || 0)}
          </Typography>
        </PositionBreakdownContainerItem>
      </PositionBreakdownContainer>
      <PositionBreakdownTitle variant="body" color="secondary">
        LP size
        <Tooltip
          content="The total value of your liquidity position, including both your own collateral and any borrowed funds."
          position="top"
        >
          <IconInfo width={16} height={16} />
        </Tooltip>
      </PositionBreakdownTitle>
      <PositionBreakdownContainer>
        <ReversedWrapper
          childX={
            <PositionBreakdownContainerItem style={isReversedPair ? {} : { marginBottom: 20 }}>
              <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                {iconUrlX && <img src={iconUrlX} width={24} height={24} alt={displaySymbolX} />}
                {displaySymbolX}
              </PositionBreakdownSymbol>
              <Typography variant="description" fontWeight="semiBold" style={{ color: theme.colors.text.description }}>
                {position.lpAmounts.x.toString()}
              </Typography>
            </PositionBreakdownContainerItem>
          }
          childY={
            <PositionBreakdownContainerItem style={isReversedPair ? { marginBottom: 20 } : {}}>
              <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                {iconUrlY && <img src={iconUrlY} width={24} height={24} alt={displaySymbolY} />}
                {displaySymbolY}
              </PositionBreakdownSymbol>
              <Typography variant="description" fontWeight="semiBold" style={{ color: theme.colors.text.description }}>
                {position.lpAmounts.y.toString()}
              </Typography>
            </PositionBreakdownContainerItem>
          }
          isReversed={isReversedPair}
        />
      </PositionBreakdownContainer>
      <PositionBreakdownTitle variant="body" color="secondary">
        Equity
        <Tooltip
          content="The value of your own capital in a position, calculated as the difference between your assets and debt."
          position="top"
        >
          <IconInfo width={16} height={16} />
        </Tooltip>
      </PositionBreakdownTitle>
      <PositionBreakdownContainer>
        <ReversedWrapper
          childX={
            <PositionBreakdownContainerItem style={isReversedPair ? {} : { marginBottom: 20 }}>
              <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                {iconUrlX && <img src={iconUrlX} width={24} height={24} alt={displaySymbolX} />}
                {displaySymbolX}
              </PositionBreakdownSymbol>
              <Typography variant="description" fontWeight="semiBold" style={{ color: theme.colors.text.description }}>
                {position.equity.x.toString()}
              </Typography>
            </PositionBreakdownContainerItem>
          }
          childY={
            <PositionBreakdownContainerItem style={isReversedPair ? { marginBottom: 20 } : {}}>
              <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                {iconUrlY && <img src={iconUrlY} width={24} height={24} alt={displaySymbolY} />}
                {displaySymbolY}
              </PositionBreakdownSymbol>
              <Typography variant="description" fontWeight="semiBold" style={{ color: theme.colors.text.description }}>
                {position.equity.y.toString()}
              </Typography>
            </PositionBreakdownContainerItem>
          }
          isReversed={isReversedPair}
        />
      </PositionBreakdownContainer>
      <PositionBreakdownTitle variant="body" color="secondary">
        Total Debt
        <Tooltip content="The total amount of funds you’ve borrowed to increase your LP size." position="top">
          <IconInfo width={16} height={16} />
        </Tooltip>
      </PositionBreakdownTitle>
      <PositionBreakdownContainer>
        <ReversedWrapper
          childX={
            <PositionBreakdownContainerItem style={isReversedPair ? {} : { marginBottom: 20 }}>
              <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                {iconUrlX && <img src={iconUrlX} width={24} height={24} alt={displaySymbolX} />}
                {displaySymbolX}
              </PositionBreakdownSymbol>
              <Typography variant="description" fontWeight="semiBold" style={{ color: theme.colors.text.description }}>
                {position.debt.x.toString()}
              </Typography>
            </PositionBreakdownContainerItem>
          }
          childY={
            <PositionBreakdownContainerItem style={isReversedPair ? { marginBottom: 20 } : {}}>
              <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                {iconUrlY && <img src={iconUrlY} width={24} height={24} alt={displaySymbolY} />}
                {displaySymbolY}
              </PositionBreakdownSymbol>
              <Typography variant="description" fontWeight="semiBold" style={{ color: theme.colors.text.description }}>
                {position.debt.y.toString()}
              </Typography>
            </PositionBreakdownContainerItem>
          }
          isReversed={isReversedPair}
        />
      </PositionBreakdownContainer>
      <PositionBreakdownTitle variant="body" color="secondary">
        Interest rates
        <Tooltip
          content="The cost of borrowing funds, which can fluctuate based on the utilization of available liquidity."
          position="top"
        >
          <IconInfo width={16} height={16} />
        </Tooltip>
      </PositionBreakdownTitle>
      <PositionBreakdownContainer>
        <ReversedWrapper
          childX={
            <div style={isReversedPair ? {} : { marginBottom: 20 }}>
              <PositionBreakdownContainerItem>
                <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                  {iconUrlX && <img src={iconUrlX} width={24} height={24} alt={displaySymbolX} />}
                  {displaySymbolX}
                </PositionBreakdownSymbol>
                <Typography
                  variant="description"
                  fontWeight="semiBold"
                  style={{ color: theme.colors.text.description }}
                >
                  {position.interestRates.x.mul(100).toDP(2, Decimal.ROUND_HALF_UP).toString()}%
                </Typography>
              </PositionBreakdownContainerItem>
              {supplyPoolXUtilization > SUPPLY_POOL_WARNING_LIMIT && (
                <Typography
                  variant="caption"
                  color="warning"
                  fontWeight="medium"
                  style={{ marginLeft: 8, marginTop: 4 }}
                >
                  {supplyPoolX.T.displaySymbol} lending pool utilization is high (
                  {getSupplyPoolUtilization(supplyPoolXUtilization)})!
                </Typography>
              )}
            </div>
          }
          childY={
            <div style={isReversedPair ? { marginBottom: 20 } : {}}>
              <PositionBreakdownContainerItem>
                <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                  {iconUrlY && <img src={iconUrlY} width={24} height={24} alt={displaySymbolY} />}
                  {displaySymbolY}
                </PositionBreakdownSymbol>
                <Typography
                  variant="description"
                  fontWeight="semiBold"
                  style={{ color: theme.colors.text.description }}
                >
                  {position.interestRates.y.mul(100).toDP(2, Decimal.ROUND_HALF_UP).toString()}%
                </Typography>
              </PositionBreakdownContainerItem>
              {supplyPoolYUtilization > SUPPLY_POOL_WARNING_LIMIT && (
                <Typography
                  variant="caption"
                  color="warning"
                  fontWeight="medium"
                  style={{ marginLeft: 8, marginTop: 4 }}
                >
                  {supplyPoolY.T.displaySymbol} lending pool utilization is high (
                  {getSupplyPoolUtilization(supplyPoolYUtilization)})!
                </Typography>
              )}
            </div>
          }
          isReversed={isReversedPair}
        />
        <PositionBreakdownContainerItem style={{ marginTop: 20, marginLeft: 2 }}>
          <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
            Effective
          </PositionBreakdownSymbol>
          <Typography variant="description" fontWeight="semiBold" style={{ color: theme.colors.text.description }}>
            {position.interestRates.effective.mul(100).toDP(2, Decimal.ROUND_HALF_UP).toString() + '%'}
          </Typography>
        </PositionBreakdownContainerItem>
      </PositionBreakdownContainer>
      <PositionBreakdownContainer style={{ marginTop: 16 }}>
        <PositionBreakdownContainerItem style={{ marginBottom: 20 }}>
          <Typography variant="description" fontWeight="semiBold">
            Estimated APR
          </Typography>
          <Typography variant="description" fontWeight="semiBold" style={{ color: theme.colors.text.description }}>
            {getAprValue(isLoading, position.status, positionMetrics?.apr1h.apr)}
          </Typography>
        </PositionBreakdownContainerItem>
        <PositionBreakdownContainerItem style={{ marginBottom: 20 }}>
          <Typography variant="description" fontWeight="semiBold">
            Leverage
          </Typography>
          <Typography variant="description" fontWeight="semiBold" style={{ color: theme.colors.text.description }}>
            {position.leverage && position.leverage.isFinite() ? position.leverage.toFixed(2) + 'x' : '-'}
          </Typography>
        </PositionBreakdownContainerItem>
        <PositionBreakdownContainerItem>
          <Typography variant="description" fontWeight="semiBold">
            Margin Level
          </Typography>
          <Typography variant="description" fontWeight="semiBold" style={{ color: theme.colors.text.description }}>
            {position.marginLevel && !position.marginLevel.isNaN()
              ? position.marginLevel.toDecimalPlaces(4).toString()
              : '-'}
          </Typography>
        </PositionBreakdownContainerItem>
      </PositionBreakdownContainer>
    </ContentContainer>
  )
}
